import { useMemo, useState } from 'react'
import {
  Badge,
  Card,
  DataTable,
  FilterBar,
  PageHeader,
  StatCard,
  FILTER_KOSONG,
  ambilBaris,
  cariAngka,
  type KolomTabel,
  type NilaiFilter,
} from '@/components'
import { formatRupiah, tanggalHariIni, awalTahunIni } from '@/lib/format'
import { useApi } from '@/lib/useApi'
import { useOpsiUnit } from '@/lib/opsi'

interface BarisNeracaSaldo {
  accountId?: string
  code?: string
  accountCode?: string
  name?: string
  accountName?: string
  accountType?: string
  debit?: number
  credit?: number
  kredit?: number
  saldoDebit?: number
  saldoKredit?: number
  [kunci: string]: unknown
}

export default function NeracaSaldo() {
  const opsiUnit = useOpsiUnit()
  const [filter, setFilter] = useState<NilaiFilter>({
    ...FILTER_KOSONG,
    dari: awalTahunIni(),
    sampai: tanggalHariIni(),
  })

  const hasil = useApi<unknown>('/trial-balance', {
    dari: filter.dari || undefined,
    sampai: filter.sampai || undefined,
    unitId: filter.unitId || undefined,
  })

  const baris = useMemo<BarisNeracaSaldo[]>(
    () => ambilBaris(hasil.data, ['rows', 'accounts', 'baris', 'items', 'data', 'lines']) as BarisNeracaSaldo[],
    [hasil.data],
  )

  const kolom: KolomTabel<BarisNeracaSaldo>[] = [
    {
      kunci: 'code',
      judul: 'Kode Akun',
      nilaiSort: (b) => String(b.code ?? b.accountCode ?? ''),
      render: (b) => <span className="font-medium text-netral-800">{String(b.code ?? b.accountCode ?? '—')}</span>,
    },
    {
      kunci: 'name',
      judul: 'Nama Akun',
      nilaiSort: (b) => String(b.name ?? b.accountName ?? ''),
      render: (b) => String(b.name ?? b.accountName ?? '—'),
    },
    {
      kunci: 'accountType',
      judul: 'Tipe',
      render: (b) => (b.accountType ? <Badge nada="info">{String(b.accountType)}</Badge> : <span className="text-netral-300">—</span>),
    },
    {
      kunci: 'debit',
      judul: 'Debit',
      rata: 'kanan',
      nilaiSort: (b) => Number(b.debit ?? b.saldoDebit ?? 0),
      render: (b) => {
        const n = Number(b.debit ?? b.saldoDebit ?? 0)
        return n ? formatRupiah(n) : <span className="text-netral-300">—</span>
      },
    },
    {
      kunci: 'credit',
      judul: 'Kredit',
      rata: 'kanan',
      nilaiSort: (b) => Number(b.credit ?? b.kredit ?? b.saldoKredit ?? 0),
      render: (b) => {
        const n = Number(b.credit ?? b.kredit ?? b.saldoKredit ?? 0)
        return n ? formatRupiah(n) : <span className="text-netral-300">—</span>
      },
    },
  ]

  const totalDebitBaris = baris.reduce((a, b) => a + Number(b.debit ?? b.saldoDebit ?? 0), 0)
  const totalKreditBaris = baris.reduce((a, b) => a + Number(b.credit ?? b.kredit ?? b.saldoKredit ?? 0), 0)

  const totalDebit = cariAngka(hasil.data, ['totalDebit', 'totalDebitAll', 'jumlahDebit']) ?? totalDebitBaris
  const totalKredit = cariAngka(hasil.data, ['totalCredit', 'totalKredit', 'jumlahKredit']) ?? totalKreditBaris
  const selisih = totalDebit - totalKredit
  const seimbang = Math.abs(selisih) < 0.005

  return (
    <div>
      <PageHeader
        judul="Neraca Saldo"
        deskripsi="Daftar saldo debit dan kredit tiap akun. Total debit HARUS sama dengan total kredit."
        ekspor={{
          path: '/reports/trial-balance',
          namaDasar: 'neraca-saldo',
          query: { dari: filter.dari || undefined, sampai: filter.sampai || undefined, unitId: filter.unitId || undefined },
        }}
      />

      <FilterBar
        nilai={filter}
        onUbah={setFilter}
        unit={opsiUnit}
        tampilkan={{ unitId: true, dari: true, sampai: true, fundId: false, program: false, kategori: false }}
      />

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label="Total Debit" nilai={totalDebit} formatNilai={(v) => formatRupiah(Number(v ?? 0))} />
        <StatCard label="Total Kredit" nilai={totalKredit} formatNilai={(v) => formatRupiah(Number(v ?? 0))} />
        <StatCard
          label="Selisih"
          nilai={selisih}
          nada={seimbang ? 'hijau' : 'merah'}
          formatNilai={(v) => formatRupiah(Number(v ?? 0))}
        />
      </div>

      <div className="mb-4">
        {seimbang ? (
          <Badge nada="sukses">Seimbang — total debit sama dengan total kredit.</Badge>
        ) : (
          <Card className="border-red-300 bg-red-50">
            <p className="text-sm font-semibold text-red-800">Neraca saldo TIDAK seimbang.</p>
            <p className="mt-1 text-sm text-red-700">
              Selisih {formatRupiah(Math.abs(selisih))} ({selisih > 0 ? 'debit lebih besar' : 'kredit lebih besar'}).
              Periksa jurnal yang belum seimbang atau saldo awal yang belum lengkap.
            </p>
          </Card>
        )}
      </div>

      <DataTable
        kolom={kolom}
        data={baris}
        kunciBaris={(b, i) => String(b.accountId ?? b.code ?? `baris-${i}`)}
        memuat={hasil.memuat}
        galat={hasil.galat}
        kodeGalat={hasil.kodeGalat}
        onCoba={hasil.muat}
        pesanKosong="Tidak ada saldo akun pada periode ini."
      />
    </div>
  )
}
